"use client";

import { useCallback, useRef, useSyncExternalStore } from "react";
import { useLiveSession } from "@/contexts/live-session-context";
import { useStudio } from "@/contexts/studio-context";
import { LiveIcon } from "../ui/atoms/icons";

/**
 * « Revenir au live », quand la vue s'est écartée de la séance.
 *
 * # Pourquoi il existe
 *
 * Le transport partagé avance chez tout le monde en même temps : c'est lui qui
 * donne son sens au tick d'une note reçue. La vue locale, elle, a le droit de
 * s'en écarter — on met en pause pour regarder un passage, on remonte à la
 * molette, on ralentit pour déchiffrer. Rien n'était prévu pour revenir, et le
 * partenaire semblait alors jouer dans le futur, sans moyen de le rattraper.
 *
 * Il ne s'affiche que pendant une séance, et seulement quand il y a
 * effectivement quelque chose à rattraper : hors live, s'écarter n'a aucune
 * conséquence, et un bouton qui ne sert à rien encombre la barre.
 *
 * # Au spectateur seul
 *
 * Qui joue ne s'écarte pas : son rouleau est fermé à la navigation, l'interprète
 * d'un récital comme chacun dans une séance à plusieurs (voir `connect` dans le
 * contexte live). Le bouton n'a donc personne d'autre à ramener, et il ne
 * s'allume jamais chez eux — ni la sonde qui le nourrit ne tourne.
 *
 * # La détection
 *
 * `isFollowingShared` est lu à intervalle plutôt que sur événement. Les écarts
 * de pause et de molette s'annoncent bien, mais un changement de vitesse ne
 * publie rien — et le faire publier un `seek` couperait les notes en train de
 * sonner, pour un bouton. Un quart de seconde suffit à un affichage, et
 * l'intervalle ne tourne que pendant la séance.
 */
export function BackToLiveButton() {
  const live = useLiveSession();
  const { studio } = useStudio();
  const notifyRef = useRef<(() => void) | null>(null);

  const onAir = live !== null && (live.status === "live" || live.status === "reconnecting");
  const local = onAir && live.isListener ? (studio?.getLocalTransport() ?? null) : null;

  const subscribe = useCallback(
    (onChange: () => void) => {
      if (local === null) {
        return () => undefined;
      }

      notifyRef.current = onChange;
      const handle = window.setInterval(onChange, 250);

      return () => {
        notifyRef.current = null;
        window.clearInterval(handle);
      };
    },
    [local],
  );

  // Une séance en pause fige la vue (`StudioSession.pause`), et la vue figée
  // ne suit plus le partagé : sans cette garde, le bouton s'allumait à chaque
  // pause, chez tout le monde. Il n'y a rien à rattraper tant que la séance ne
  // défile pas ; la reprise rend au direct une vue qui le suivait.
  const readAdrift = useCallback(
    () => (local === null ? false : !local.isFollowingShared && !(studio?.getSession().isPaused ?? false)),
    [local, studio],
  );

  const adrift = useSyncExternalStore(subscribe, readAdrift, () => false);

  const handleClick = useCallback(() => {
    local?.followShared();
    // Sans ça le bouton resterait affiché jusqu'au prochain battement.
    notifyRef.current?.();
  }, [local]);

  if (!adrift) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      title="Votre vue s'est écartée du direct. Revenir à l'instant que tout le monde partage."
      /* Même gabarit que le bouton live voisin. */
      className="relative inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[12px] font-medium tracking-wide transition-colors duration-150 cursor-pointer text-amber-300 bg-amber-500/10 hover:bg-amber-500/15 pointer-coarse:after:absolute pointer-coarse:after:content-[''] pointer-coarse:after:-inset-y-2 pointer-coarse:after:inset-x-0"
    >
      <LiveIcon size={13} />
      <span>Revenir au live</span>
    </button>
  );
}
